<template>
  <AppDialog
    v-if="showDialog"
    :show="showDialog"
    append-to-body
    size="large"
  >
    <div class="d-flex align-items-center widget-title">
      {{ card.title }}
      <button
        class="close ml-auto flex"
        type="button"
        data-dismiss="modal"
        aria-hidden="true"
        @click="toggleDialog"
      >
        <i class="hi hi-close hi-12"></i>
      </button>
    </div>

    <div class="insights-github-integration-cards-dialog-info">
      <div v-if="card.key === 'time_to_merge'">
        <div class="time-to-merge">
          <div class="metric">
            <h5>Mean</h5>
            <div class="value">{{ formatValue(card.mean) }}</div>
          </div>
          <div class="metric">
            <h5>95th p.</h5>
            <div class="value">{{ formatValue(card.percentale) }}</div>
          </div>
        </div>
      </div>
      <span
        v-else
        class="title"
      >
        {{ formatValue(card) }}
      </span>

      <div class="description">
        <i class="hi hi-info" />
        <div>{{ card.description }}</div>
      </div>
    </div>

    <AppInfiniteScroll
      ref="scroller"
      max-height="60vh"
      @loadmore="loadMoreData"
    >
      <InsightsGitHubIntegrationCardsTable
        :table-data="tableData"
        :loading="loading || localLoading"
        :lazy-loading="lazyLoading"
        :sort-options="sortOptions"
        :show-metrics="showMetrics"
        @sort="sort"
        @row:click="handleRowClick"
      />
    </AppInfiniteScroll>
  </AppDialog>
</template>

<script>
import dayjs from 'dayjs';
import { mapActions, mapGetters } from 'vuex';
import { Insights } from '~/mixins';
import InsightsGitHubIntegrationCardsTable from '~/components/insights/InsightsGitHubIntegrationCardsTable.vue';

const WIDGET_KEY = 'git_hostings_pull_requests';

export default {
  name: 'InsightsGitHubIntegrationCardsDialog',
  components: {
    InsightsGitHubIntegrationCardsTable,
  },
  mixins: [Insights],
  props: {
    showDialog: {
      type: Boolean,
      required: true,
    },
    card: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localLoading: true,
      showMetrics: false,
      tableData: [],
      offset: 0,
      limit: 10,
      isFullyLoaded: false,
      sortOptions: {},
      tooltipOptions: Object.freeze({
        placement: 'top',
      }),
    };
  },
  computed: {
    ...mapGetters(['loadingWidgets', 'lazyLoadingWidgets']),
    hasData() {
      return !!(this.tableData && this.tableData.length);
    },
    loading() {
      return !!this.loadingWidgets[WIDGET_KEY];
    },
    lazyLoading() {
      return !!this.lazyLoadingWidgets[WIDGET_KEY];
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    ...mapActions(['fetchWidgets', 'setFullyLoaded']),
    async loadData(options = { lazy: false }) {
      try {
        const sortOptions = {
          sort_by: this.sortOptions.field,
          sort_order: this.sortOptions.direction,
        };
        const response = await this.fetchWidgets({
          widgetKey: WIDGET_KEY,
          extraQueryParams: {
            limit: this.limit,
            offset: this.offset,
            integration_type: 'github',
            type: this.card.key,
            ...sortOptions,
          },
          ...options,
        });
        const data = response[WIDGET_KEY]?.data;

        if (data) {
          this.tableData = [...(this.offset ? this.tableData : []), ...data];
          this.isFullyLoaded = data.length < this.limit;
        }
        this.localLoading = false;

        return Promise.resolve();
      } catch (e) {
        console.log(e);
      }
    },
    async loadMoreData() {
      if (!this.lazyLoading && !this.loading && !this.isFullyLoaded) {
        const dataLength = this.tableData.length;

        this.offset = dataLength;
        await this.loadData({ lazy: true });
      }
    },
    async sort(options) {
      // Clean up the scrolled down table and hide the empty state when sorting
      this.localLoading = true;
      this.tableData = [];
      this.sortOptions = options;
      this.offset = 0;
      this.$nextTick(() => this.loadData());
    },
    formatValue(card) {
      switch (card.valueType) {
        case 'strict_count':
          return card.value ? Math.floor(card.value) : 0;
        case 'count':
          return card.value ? card.value.toFixed(2) : 0;
        case 'percentage':
          return `${Math.round(card.value)}%`;
        case 'duration':
          // eslint-disable-next-line no-case-declarations
          const duration = dayjs.duration(card.value, 'seconds');

          if (duration.days() === 0) {
            return `${duration.hours()}h`;
          }
 
            return `${duration.days()}d ${duration.hours()}h`;
          
        default:
          return card.value || '-';
      }
    },
    toggleDialog() {
      this.$emit('closeDialog');
    },
    handleRowClick(row) {
      // TODO make v-track work
      analytics?.track('Output link selected', {
        label: 'Widget hover data',
        widget_name: `GitHub ${this.card.title}`,
        datatype: 'Card',
        url: row.url,
      });
    },
  },
};
</script>
